/**
 * Data-only custom element that fetches product data via the Storefront API
 * for a list of variant handles stored in a product metafield.
 * Dispatches a 'products:loaded' event on document when data is ready.
 * Other custom elements can listen for this event to consume the data.
 */
class PoVariantHandlesProducts extends HTMLElement {

  /** @type {number} Max aliases per GraphQL request */
  static BATCH_SIZE = 20;

  /**
   * Splits an array into chunks of the given size.
   * @param {Array} array - Source array.
   * @param {number} size - Max items per chunk.
   * @returns {Array[]} Array of chunks.
   */
  static chunk(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  constructor() {
    super();

    this.handles = (this.dataset.handles || '')
      .split(',')
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean);

    this.excludeHandle = this.dataset.excludeHandle;
    this.productPrices = JSON.parse(this.dataset.productPrices || '{}');
    this.products = null;
    this.dkContext = this.buildDkContext();
  }

  buildDkContext() {
    const appliedCodes =
      document.documentElement?.dataset?.poDkAppliedCodes ||
      this.dataset.dkAppliedCodes ||
      (typeof window.PoDkStorefrontPrices?.getAppliedCodesCsv === 'function'
        ? window.PoDkStorefrontPrices.getAppliedCodesCsv()
        : '') ||
      '';

    return {
      market: (
        this.dataset.dkMarket ||
        window.poDkStorefrontContext?.market ||
        ''
      )
        .toLowerCase()
        .trim(),
      currency: (
        this.dataset.dkCurrency ||
        window.poDkStorefrontContext?.currency ||
        ''
      )
        .toUpperCase()
        .trim(),
      appliedCodes,
    };
  }

  async connectedCallback() {
    if (this.excludeHandle) {
      this.handles = this.handles.filter((h) => h !== this.excludeHandle);
    }

    const publishState = (payload) => {
      window.variantsHandleGraphData = payload;
      window.dispatchEvent(
        new CustomEvent('variantsHandleGraphLoaded', {
          detail: payload
        })
      );
    };

    if (!this.handles.length) {
      publishState({ products: [], loaded: true, dkContext: this.buildDkContext() });
      return;
    }

    try {
      this.products = await this.fetchProducts(this.handles);
      publishState({
        products: this.products,
        loaded: true,
        dkContext: this.buildDkContext(),
      });
    } catch (error) {
      console.error('[PoVariantHandlesProducts] Failed to fetch products', error);
      publishState({ products: [], loaded: true, error, dkContext: this.buildDkContext() });
    }
  }

  /**
   * Fetches products in parallel batches.
   * @param {string[]} handles - Product handles to query.
   * @returns {Promise<Object[]>} Resolved array of product objects.
   */
  async fetchProducts(handles) {
    const { url, token } = window.storefront || {};

    if (!url || !token) {
      throw new Error('[PoVariantHandlesProducts] Storefront credentials not available');
    }

    const { BATCH_SIZE, chunk } = PoVariantHandlesProducts;
    const batches = chunk(handles, BATCH_SIZE);
    // const batches = PoVariantHandlesProducts.chunk(handles, PoVariantHandlesProducts.BATCH_SIZE);

    const results = await Promise.all(
      batches.map((batch) => this.fetchBatch(url, token, batch))
    );

    return results.flat();
  }

  /**
   * Executes a single batched GraphQL request.
   * @param {string} url - Storefront API endpoint.
   * @param {string} token - Storefront access token.
   * @param {string[]} handles - Batch of product handles.
   * @returns {Promise<Object[]>} Products from this batch.
   */
  async fetchBatch(url, token, handles) {
    const productFragment = `
      fragment ProductFields on Product {
        id
        title
        handle
        availableForSale
        productType
        color: metafield(namespace: "custom", key: "color") {
          value
        }
        lensColorDescription: metafield(namespace: "custom", key: "lens_color_description") {
          value
        }
        polarized: metafield(namespace: "custom", key: "polarized") {
          value
        }
        colorCode: metafield(namespace: "custom", key: "color_code") {
          value
        }
        brandline: metafield(namespace: "custom", key: "brandline") {
          value
        }
        colorswatchImage: metafield(namespace: "custom", key: "colorswatch_image") {
          reference {
            ... on MediaImage {
              image {
                url
                altText
                width
                height
              }
            }
          }
        }
        dkDiscounts: metafield(namespace: "app--9549316097--discount_kit", key: "discounts") {
          value
        }
        featuredImage {
          url
          altText
          width
          height
        }
        variants(first: 50) {
          nodes {
            id
            title
            barcode
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            lensWidth: metafield(namespace: "custom", key: "lens_width") {
              value
            }
            bridgeWidth: metafield(namespace: "custom", key: "bridge_width") {
              value
            }
            newReleases: metafield(namespace: "custom", key: "newreleases") {
              value
            }
          }
        }
      }
    `;

    const aliases = handles
      .map(
        (handle, i) =>
          `product${i}: product(handle: "${handle}") { ...ProductFields }`
      )
      .join('\n');

    const rawCountry = window.Shopify?.country || 'US';
    const country = rawCountry.split('-')[0].toUpperCase();
    const rawLocale = window.Shopify?.locale || 'en';
    const language = rawLocale.split('-')[0].toUpperCase();
    const query = productFragment + '\nquery @inContext(country: ' + country + ', language: ' + language + ') { ' + aliases + ' }';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': window.Shopify.locale || 'en',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      throw new Error(`Storefront API responded with ${response.status}`);
    }

    const { data, errors } = await response.json();

    if (errors && errors.length) {
      console.warn('[PoVariantHandlesProducts] GraphQL errors', errors);
    }

    const baseUrl = window.location.origin + window.Shopify.routes.root + 'products/';
    if (!data || typeof data !== 'object') {
      console.warn('[PoVariantHandlesProducts] Empty GraphQL data payload', { country, language, errors });
      return [];
    }
    // Get the products from the data
    const products = Object.values(data).filter(Boolean);

    // Add the base URL to the product object
    return products.map(function (product) {
      // Add the base URL to the product object
      product.url = baseUrl + product.handle;
      product.marketPrice = this.productPrices[product.handle];
      // Filter out RX variants and keep only the non-RX variants 
      product.variants.nodes = product.variants.nodes.filter(function (variant) {
        const isRx = variant.title.toUpperCase().includes('RX');
        return !isRx;
      });
      return product;
    }, this);
  }
}

customElements.define('po-variant-handles-products', PoVariantHandlesProducts);
